import { useSyncExternalStore } from 'react';

// Real member auth, backed by the self-hosted GoTrue service (accounts live in
// our own Cloud SQL). The public interface (getSession / useSession / signOut)
// is unchanged from the old mock so existing callers (AuthGate, headers, gates)
// keep working; signIn is now a no-op shim — use signUp / login instead.
const SUPABASE_URL = (process.env.REACT_APP_SUPABASE_URL
  || 'https://yuftrqcahapyrblxvkns.supabase.co').replace(/\/$/, '');
// Public anon key — safe to ship in the bundle; every Supabase auth/data
// request must carry it as the `apikey` header or the gateway returns 401.
export const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY
  || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inl1ZnRycWNhaGFweXJibHh2a25zIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODk3ODY4ODksImV4cCI6MjEwNTM2Mjg4OX0.c9bnJ0ODxlKE3MqG3v2Gw4t6zz89CIjUlSrR3gReHUI';
export const GOTRUE_URL = (process.env.REACT_APP_GOTRUE_URL
  || `${SUPABASE_URL}/auth/v1`).replace(/\/$/, '');
const KEY = 'ihsanone_session';

const listeners = new Set();
let cached;
let cacheRaw = null;

function read() {
  try { return localStorage.getItem(KEY); } catch { return null; }
}

function write(session) {
  try {
    if (session) localStorage.setItem(KEY, JSON.stringify(session));
    else localStorage.removeItem(KEY);
  } catch { /* private mode — session simply won't persist */ }
  emit();
}

function emit() { listeners.forEach((l) => l()); }

// getSnapshot must return a stable reference between renders, so the parsed
// view is memoised against the raw string it came from.
function getSnapshot() {
  const raw = read();
  if (raw !== cacheRaw) {
    cacheRaw = raw;
    try {
      const s = raw ? JSON.parse(raw) : null;
      cached = s
        ? {
          name: s.user_meta?.full_name || s.email || 'Member',
          email: s.email,
          uid: s.uid,
          // Settings reads the consent captured at sign-up from here. Member-
          // editable, so it is the control surface, never the evidence — the
          // consent_ledger is that.
          meta: s.user_meta || {},
        }
        : null;
    } catch { cached = null; }
  }
  return cached;
}

function subscribe(listener) {
  listeners.add(listener);
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

function currentSession() {
  try { const r = read(); return r ? JSON.parse(r) : null; } catch { return null; }
}

async function post(path, body) {
  const res = await fetch(`${GOTRUE_URL}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', apikey: SUPABASE_ANON },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

function storeFromGoTrue(data) {
  if (!data || !data.access_token) return false;
  const s = {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    expires_at: Date.now() + (data.expires_in || 3600) * 1000,
    uid: data.user && data.user.id,
    email: data.user && data.user.email,
    user_meta: (data.user && data.user.user_metadata) || {},
  };
  write(s);
  scheduleRefresh(s);
  return true;
}

// Exported alias — same session-storing logic as storeFromGoTrue, for callers
// outside this module (e.g. ContinueGate, after it verifies a magic-link
// token_hash) that need to persist a GoTrue token response.
export function storeSession(gotrueData) {
  return storeFromGoTrue(gotrueData);
}

// ---- public API ----

export function getSession() {
  return getSnapshot();
}

// The signed-in member's contact identity (for creating a real VMS application).
export function getMemberIdentity() {
  const s = currentSession();
  if (!s) return null;
  const meta = s.user_meta || {};
  return {
    email: s.email || '',
    fullName: meta.full_name || meta.name || s.email || 'Member',
    mobile: meta.mobile || '',
  };
}

export async function signUp(email, password, meta = {}) {
  const { ok, data } = await post('/signup', { email, password, data: meta });
  if (!ok) return { error: data.msg || data.error_description || 'Sign-up failed' };
  // With mailer_autoconfirm off, Supabase returns the user record but no
  // access_token — the account must be confirmed via the emailed link (or
  // 6-digit code) before login works. Callers should show a "check your
  // email" screen when needsConfirmation is true.
  storeFromGoTrue(data);
  return { error: null, needsConfirmation: !data.access_token, email };
}

export async function login(identifier, password) {
  // GoTrue password grant is email-based; mobile login needs phone auth (later).
  const { ok, data } = await post('/token?grant_type=password', { email: identifier, password });
  if (!ok || !data.access_token) {
    // GoTrue returns error_code="email_not_confirmed" when the account exists
    // but the confirmation link hasn't been tapped. Surface it as a typed
    // signal so the UI can offer a "Resend confirmation email" action.
    const err = data.error_code || data.error || '';
    const msg = data.msg || data.error_description || data.error || 'Invalid email or password';
    if (err === 'email_not_confirmed' || /not confirmed|email.*confirm/i.test(msg)) {
      return { error: 'Please confirm your email first — check your inbox.', needsConfirmation: true, email: identifier };
    }
    return { error: msg };
  }
  storeFromGoTrue(data);
  return { error: null };
}

// PKCE-style signup confirmation. The confirmation email points our app at
// `?confirm=1#th=<token_hash>&em=<email>`; ContinueGate exchanges the
// token_hash for a session ONLY after a real human tap (mail-scanner-safe,
// same pattern as verifyMagicToken above). Returns true/false so the gate
// can just branch.
export async function verifyConfirmToken(tokenHash) {
  if (!tokenHash) return false;
  try {
    const { ok, data } = await post('/verify', { type: 'signup', token_hash: tokenHash });
    if (!ok || !data || !data.access_token) return false;
    storeFromGoTrue(data);
    return true;
  } catch {
    return false;
  }
}

// Ask GoTrue to resend the confirmation email for a signup that already exists
// but hasn't been confirmed. Returns { ok, rateLimited?, error? } — same shape
// as requestEmailOtp so the UI can share code.
export async function resendConfirmation(email) {
  const { ok, data } = await post('/resend', { type: 'signup', email });
  if (ok) return { ok: true };
  const message = data?.msg || data?.error_description || data?.error || '';
  if (data?.error_code === 'over_email_send_rate_limit' || /rate limit|too many/i.test(message)) {
    return { ok: false, rateLimited: true, error: 'Too many requests. Try again in a minute.' };
  }
  return { ok: false, error: message || 'Could not resend the confirmation.' };
}

// Ask GoTrue to email a password-reset link. The template is wired PKCE-safe as
// `?recover=1#th=<token_hash>&em=<email>` — RecoverGate exchanges the hash for a
// short-lived session only after a real click, then lets the user set a new
// password (which invalidates every other session for the account server-side).
//
// Returns { ok, rateLimited?, error? }. Mirrors resendConfirmation so callers
// can share the same "check your inbox / try again in a minute" copy.
//
// SECURITY: on a "no such user" response we return { ok: true } — the caller
// must NEVER say whether an address has an account (charity-membership is
// itself sensitive). GoTrue on Supabase already responds 200 for the unknown-
// user case, but we defend against future behaviour changes here explicitly.
export async function sendPasswordReset(email) {
  const clean = String(email || '').trim();
  if (!clean) return { ok: false, error: 'Please enter your email address.' };
  const { ok, data } = await post('/recover', { email: clean });
  if (ok) return { ok: true };
  const message = data?.msg || data?.error_description || data?.error || '';
  if (/not found|user.*not|no user/i.test(message)) return { ok: true };
  if (data?.error_code === 'over_email_send_rate_limit' || /rate limit|too many/i.test(message)) {
    return { ok: false, rateLimited: true, error: 'Too many requests. Try again in a minute.' };
  }
  return { ok: false, error: message || 'Could not send the reset email.' };
}

// PKCE-style recovery token exchange. Same shape as verifyConfirmToken /
// verifyMagicToken: on success it writes a session (so a subsequent updateUser
// call is authenticated as the account being reset); on failure it returns
// false so the gate can show "link expired or already used".
export async function verifyRecoverToken(tokenHash) {
  if (!tokenHash) return false;
  try {
    const { ok, data } = await post('/verify', { type: 'recovery', token_hash: tokenHash });
    if (!ok || !data || !data.access_token) return false;
    storeFromGoTrue(data);
    return true;
  } catch {
    return false;
  }
}

// Sets a new password on the currently-authenticated account (GoTrue PUT /user).
// Called by RecoverGate after verifyRecoverToken has established a recovery
// session. Returns { ok, error? }. A successful password change invalidates
// every other refresh token on the account (Supabase default), so an attacker
// still holding an old session loses it as soon as the real owner completes
// the reset.
export async function updatePassword(newPassword) {
  const s = currentSession();
  if (!s || !s.access_token) return { ok: false, error: 'Your reset link expired — request a new one.' };
  try {
    const res = await fetch(`${GOTRUE_URL}/user`, {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        apikey: SUPABASE_ANON,
        Authorization: `Bearer ${s.access_token}`,
      },
      body: JSON.stringify({ password: newPassword }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const message = data?.msg || data?.error_description || data?.error || 'Could not update your password.';
      return { ok: false, error: message };
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, error: 'Network error — please try again.' };
  }
}

// Verify-on-click magic link: the emailed link carries a GoTrue token_hash in
// the URL fragment (never sent to servers/logs) instead of a ready-to-consume
// access_token, so a mail-security scanner prefetching the link can't burn the
// one-tap sign-in. ContinueGate calls this only after a real human clicks
// "Continue". Returns true/false rather than throwing — callers just branch
// on the result.
export async function verifyMagicToken(tokenHash) {
  if (!tokenHash) return false;
  try {
    const { ok, data } = await post('/verify', { type: 'magiclink', token_hash: tokenHash });
    if (!ok || !data || !data.access_token) return false;
    storeFromGoTrue(data);
    return true;
  } catch {
    return false;
  }
}

// ---- passwordless: email a link AND a 6-digit code ----
// GoTrue emails both from one /otp call, provided the mail template carries
// {{ .Token }} (the code) as well as the ?continue=1#th={{ .TokenHash }} link.
// Whichever the member uses, they end up signed in — which matters because an
// installed iOS PWA has its own storage: a link tapped in Mail opens Safari and
// logs THAT in, leaving the installed app signed out. Typing the code inside
// the app is the only thing that fixes it.

export async function requestEmailOtp({ email, createUser = false, data } = {}) {
  const body = { email, create_user: Boolean(createUser) };
  if (data) body.data = data;
  const { ok, data: res } = await post('/otp', body);
  if (ok) return { ok: true };

  const message = res?.msg || res?.error_description || res?.error || '';
  // On log-in, "no such user" must look identical to success. Membership of a
  // Muslim charity's app is itself sensitive, so the form never confirms
  // whether an address has an account.
  if (!createUser && /not found|signups? not allowed|user not/i.test(message)) {
    return { ok: true };
  }
  if (res?.error_code === 'over_email_send_rate_limit' || /rate limit|too many/i.test(message)) {
    return { ok: false, rateLimited: true, error: 'Too many requests. Try again in a minute.' };
  }
  return { ok: false, error: message || 'Could not send the email.' };
}

export async function verifyEmailCode(email, code) {
  // Which verify type a GoTrue build accepts varies; try the most specific
  // first and fall back rather than guessing at the deployed version.
  for (const type of ['email', 'magiclink', 'signup']) {
    // eslint-disable-next-line no-await-in-loop
    const { ok, data } = await post('/verify', { type, email, token: code });
    if (ok && data?.access_token) {
      storeFromGoTrue(data);
      return true;
    }
  }
  return false;
}

// Legacy shim — auth is real now; kept so any stray caller doesn't crash.
export function signIn() { /* replaced by signUp / login */ }

export function signOut() {
  const s = currentSession();
  if (s && s.access_token) {
    fetch(`${GOTRUE_URL}/logout`, {
      method: 'POST', headers: { apikey: SUPABASE_ANON, Authorization: `Bearer ${s.access_token}` },
    }).catch(() => {});
  }
  write(null);
}

// "Sign out of all devices" — server-enforced revocation, not just local storage.
//
// Vanilla `POST /auth/v1/logout?scope=global` invalidates every REFRESH token
// on the account, but the current ACCESS tokens (≤1 h lifetime) remain
// cryptographically valid until they expire — so a stolen bearer would still
// work everywhere for that window. We close that window with a DB-side
// revocation timestamp (`public.session_revocations.revoked_before`) that
// every protected surface checks:
//   * PostgREST RLS uses `session_uid()` → returns NULL for a token issued
//     before the revocation → row-level SELECTs come back empty.
//   * Writer RPCs (`redeem_reward`, `push_subscribe`, …) call
//     `assert_session_valid()` at the top → raise `session_revoked`.
//   * VMS `/api/member/*` compares `iat` against the revocation row → 401.
//   * ihsanone-pay `/create-checkout` does the same → 401 `session_revoked`.
//
// Order:
//   1. Call the RPC while we still have a valid token — the RPC records the
//      revocation and ALSO revokes refresh tokens + drops session rows
//      inside `auth`.
//   2. Fire GoTrue's `?scope=global` as a belt-and-braces (in case the RPC
//      raced with a fresh refresh — GoTrue's own tables are authoritative
//      for the refresh path).
//   3. Wipe local storage.
//
// Returns { ok, error?, revoked_before? }. Always wipes local storage on
// success; on failure the caller decides whether to retry.
export async function signOutAllDevices() {
  const s = currentSession();
  if (!s || !s.access_token) {
    write(null);
    return { ok: true, revoked_before: null };
  }
  try {
    const rpc = await fetch(`${SUPABASE_URL}/rest/v1/rpc/revoke_all_sessions`, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON,
        Authorization: `Bearer ${s.access_token}`,
        'content-type': 'application/json',
        Accept: 'application/json',
      },
      body: '{}',
    });
    const data = await rpc.json().catch(() => ({}));
    if (!rpc.ok) {
      return { ok: false, error: data?.message || data?.msg || `revoke_all_sessions failed (HTTP ${rpc.status})` };
    }
    // Belt-and-braces global logout — pushes the refresh-token nuke through
    // GoTrue's own path too. Best-effort; the DB RPC already did the work.
    try {
      await fetch(`${GOTRUE_URL}/logout?scope=global`, {
        method: 'POST',
        headers: { apikey: SUPABASE_ANON, Authorization: `Bearer ${s.access_token}` },
      });
    } catch { /* no-op */ }
    write(null);
    return { ok: true, revoked_before: data?.revoked_before || null,
             refresh_tokens_revoked: data?.refresh_tokens_revoked,
             sessions_deleted: data?.sessions_deleted };
  } catch (e) {
    return { ok: false, error: 'Network error — please try again.' };
  }
}

export function useSession() {
  return useSyncExternalStore(subscribe, getSnapshot, () => null);
}

// Returns a valid access token, refreshing if it's within a minute of expiry.
// Data calls (PostgREST) use this to attach the Bearer token.
export async function getAccessToken() {
  const s = currentSession();
  if (!s) return null;
  if (Date.now() < s.expires_at - 60000) return s.access_token;
  const { ok, data } = await post('/token?grant_type=refresh_token', { refresh_token: s.refresh_token });
  if (ok && data.access_token) { storeFromGoTrue(data); return data.access_token; }
  write(null);
  return null;
}

let refreshTimer = null;
function scheduleRefresh(s) {
  if (refreshTimer) clearTimeout(refreshTimer);
  const ms = Math.max(5000, s.expires_at - Date.now() - 60000);
  refreshTimer = setTimeout(() => { getAccessToken(); }, ms);
}

// ---- magic-link callback (Swapcard-style "click email link -> already signed in") ----
// Our self-hosted GoTrue sends the member back with the session in the URL
// FRAGMENT (implicit flow), never the query string:
//   https://ihsanone.org/?claim=<TOKEN>#access_token=...&refresh_token=...&expires_in=3600&token_type=bearer&type=magiclink
// No external JWT lib — decode the access_token payload inline.
function base64UrlDecode(input) {
  let str = input.replace(/-/g, '+').replace(/_/g, '/');
  while (str.length % 4) str += '=';
  const bin = atob(str);
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  return new TextDecoder('utf-8').decode(bytes);
}

function decodeJwtPayload(token) {
  const part = token.split('.')[1];
  if (!part) return null;
  try { return JSON.parse(base64UrlDecode(part)); } catch { return null; }
}

// Runs once at module load — before React mounts — so useSession() already
// reflects the new session by the time AuthGate/ClaimGate render.
function consumeMagicLinkFragment() {
  try {
    const rawHash = window.location.hash || '';
    if (!rawHash || rawHash.indexOf('access_token') === -1) return;
    const params = new URLSearchParams(rawHash.replace(/^#/, ''));
    const access_token = params.get('access_token');
    const refresh_token = params.get('refresh_token');
    if (!access_token || !refresh_token) return;
    const expires_in = Number(params.get('expires_in')) || 3600;

    // Same session shape storeFromGoTrue writes, sourced from the JWT since
    // the implicit-flow fragment carries no separate `user` object.
    const payload = decodeJwtPayload(access_token) || {};
    const s = {
      access_token,
      refresh_token,
      expires_at: Date.now() + expires_in * 1000,
      uid: payload.sub,
      email: payload.email,
      user_meta: payload.user_metadata || {},
    };
    write(s);
    scheduleRefresh(s);

    // Strip the GoTrue auth params from the hash. Preserve a `claim=` entry
    // if one happens to be riding in the hash too (ClaimGate accepts
    // #claim=<token> as a fallback to ?claim=), and never touch
    // location.search — that's where ?claim=<token> normally lives and
    // ClaimGate reads it from there independently.
    const claim = params.get('claim');
    const newHash = claim ? `claim=${encodeURIComponent(claim)}` : '';
    const newUrl = window.location.pathname + window.location.search + (newHash ? `#${newHash}` : '');
    window.history.replaceState(window.history.state, '', newUrl);
  } catch {
    /* no-op — malformed fragment, blocked storage (private mode), etc. */
  }
}

consumeMagicLinkFragment();

// On load, keep an existing session fresh.
(() => { const s = currentSession(); if (s) scheduleRefresh(s); })();
